import "./App.css";
import CreateAssessLayout from "./components/Assessment/Layout/CreateAssessLayout";
import Assessment from "./pages/Assessment";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import AssessmentReview from "./pages/Assessment/sections/AssessmentReview";
import AssessmentSetting from "./pages/Assessment/sections/AssessmentSetting";
import CreateAssessmentResponseList from "./pages/CreatedAssessListAndReview/CreateAssessmentResponseList/CreateAssesmentResponseList";
import CreatedAssessmentList from "./pages/CreatedAssessListAndReview/CreatedAssessmentList/CreatedAssessmentList";
import CreateAssessmentReview from "./pages/CreatedAssessListAndReview/CreateAssessmentReview/CreateAssessmentReview";
import {
  extractLanguage,
  geoLocationapiClient,
  getBrowserName,
  handleLogout,
  playNotificationSound,
} from "./utils/Common/Constant";
import { useEffect } from "react";
import i18n from "./i18nConfig";
import {
  PrivateCompanyRoute,
  PublicCompanyRoute,
} from "./utils/AuthRoutes/AuthRoutes";
import NotFound from "./pages/NotFound/NotFound";
import Unauthorized from "./pages/Unauthorized/Unauthorized";
import AssessmentCustom from "./pages/Assessment/sections/AssessmentCustom";
import AssessmentBuild from "./pages/Assessment/sections/AssessmentBuild";
import Login from "./pages/Assessment/Auth/Login";
import SignupScreen from "./pages/Assessment/Auth/SignupScreen";
import VerifyOtpScreen from "./pages/Assessment/Auth/VerifyOtpScreen";
import CreatePasswordScreen from "./pages/Assessment/Auth/CreatePasswordScreen";
import CreateProfileScreen from "./pages/Assessment/Auth/CreateProfile/CreateProfileScreen";
import AccountScreen from "./pages/Accounts/AccountScreen";
import SampleQuestions from "./pages/Assessment/SampleQuestions/SampleQuestions";
import ForgotPassword from "./pages/Assessment/Auth/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/Assessment/Auth/ResetPassword/ResetPassword";
import StripeConfirmOtp from "./pages/Accounts/Checkout/StripeConfirmOtp";
import CvScanning from "./pages/CvScanning";
import JobDescription from "./pages/CvScanning/JobDescription/JobDescription";
import SideBarTabContent from "./pages/CvScanning/AssessmentList/DetailsPages/SidebarTab/SideBarTabContent";
import Mailbox from "./pages/Assessment/Auth/Mailbox";
import LinkedInCallback from "./pages/Assessment/Auth/LinkedinLogin/LinkedInCallback";
import JobShare from "./pages/CvScanning/JobShare/JobShare";
import ChangePassword from "./pages/ChangePassword/ChangePassword";
import { getCompanyProfileDetails, logoutApi } from "./service/Scope/Scope";
import AllVendors from "./pages/AllVendors/AllVendors";
import Contracts from "./pages/Contracts/Contracts";
import ContractDetailSidebar from "./pages/Contracts/ContractDetailSidebar.tsx/ContractDetailSidebar";
import Application from "./pages/Applications/Application";
import Settings from "./pages/Setting/Settings";
import { initializeApiClient } from "./service/Connection/Connection";
import VendorDocuments from "./pages/AllVendors/DocumentReview/VendorDocuments";
import { CommonEnums } from "./utils/Enums/CommonEnums";
import Integration from "./pages/Setting/Integration/Integrations";
import { generateToken, messaging } from "./PushNotification/firebase";
import { onMessage } from "firebase/messaging";
import DetailedReport from "./pages/CreatedAssessListAndReview/AssessmentReport/DetailedReport/DetailedReport";
function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem("companyToken") ? true : false;
  const companyUserId = localStorage.getItem("companyUserId");

  //get ip and geolocation address
  const getIp = async () => {
    const geo = await geoLocationapiClient();
    localStorage.setItem("ipAddress", geo?.ip);
  };
  useEffect(() => {
    getIp();
  }, []);

  const currentLanguage =
    localStorage.getItem("currentLanguage") ?? CommonEnums.LANGUAGE_EN;
  const timeInterval = 60 * 1000; // to call api in every 1 min
  // profile api call to get if status is 401 then remove token and navigate to login page
  const getProfileDetails = async () => {
    const logoutApiCall = async () => {
      const res = await logoutApi({
        sessionId: localStorage.getItem("sessionId"),
      });
      if (res?.status === 200) {
        handleLogout(navigate);
      }
    };
    const res: any = await getCompanyProfileDetails(companyUserId);
    if (res?.status === 401) {
      logoutApiCall();
    }
  };
  useEffect(() => {
    if (!isLoggedIn) return;
    getProfileDetails();
    const interval = setInterval(() => {
      (async () => {
        await getProfileDetails();
      })();
    }, timeInterval);
    return () => clearInterval(interval);
  }, [isLoggedIn]);
  useEffect(() => {
    const language: any = extractLanguage(location.pathname);
    i18n.changeLanguage(language);
    initializeApiClient(language);
    if (!localStorage.getItem("currentLanguage")) {
      localStorage.setItem("currentLanguage", CommonEnums.LANGUAGE_EN);
    }
    const rtlLanguages = ["ar", "ur", "fa", "he"];
    document.documentElement.dir = rtlLanguages.includes(language)
      ? "rtl"
      : "ltr";
  }, [location.pathname]);
  useEffect(() => {
    if (getBrowserName() !== CommonEnums.BROWSER_SAFARI) {
      generateToken();
      const unsubscribe = onMessage(messaging, (payload: any) => {
        const data = payload.data || {};
        const notificationData = {
          title: data.title || "New Notification",
          body: data.body || "",
          icon: data.icon || "/favicon.ico",
          url: data.redirectUrl || "/",
        };
        if (Notification.permission === "granted") {
          const notification = new Notification(notificationData.title, {
            body: notificationData.body,
            icon: notificationData.icon,
            data: { url: notificationData.url },
          });
          notification.onclick = (event: any) => {
            window.focus();
            const targetUrl = event?.target?.data?.url || notificationData.url;
            window.location.href = targetUrl;
          };
          playNotificationSound();
        }
      });
      return () => unsubscribe();
    }
  }, []);
  const slabID = location?.state?.rechargeSlabID ?? "";
  const tempID = location?.state?.templateID ?? "";
  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            <Navigate
              to={`/${currentLanguage}/company-login${slabID ? `?sId=${slabID}` : ""}${tempID ? `?tempId=${tempID}` : ""}`}
              replace
            />
          }
        />
        {/* parent which code will always appear to its child */}
        <Route
          path="/:languageParam/assessment"
          element={
            <PrivateCompanyRoute>
              <CreateAssessLayout />
            </PrivateCompanyRoute>
          }
        >
          <Route
            path="/:languageParam/assessment/template/:tempUniqueId"
            element={<Assessment />}
          />
          <Route
            path="/:languageParam/assessment/build/:tempUniqueId"
            element={<AssessmentBuild />}
          />
          <Route
            path="/:languageParam/assessment/custom/:tempUniqueId"
            element={<AssessmentCustom />}
          />
          <Route
            path="/:languageParam/assessment/settings/:tempUniqueId"
            element={<AssessmentSetting />}
          />
          <Route
            path="/:languageParam/assessment/review/:tempUniqueId"
            element={<AssessmentReview />}
          />
          <Route
            path="/:languageParam/assessment/list"
            element={<CreatedAssessmentList />}
          />
          <Route
            path="/:languageParam/assessment/reviewlist/:assessmentIdParam"
            element={<CreateAssessmentReview />}
          />
          <Route
            path="/:languageParam/assessment/response/:assessmentIdParam"
            element={<CreateAssessmentResponseList />}
          />
          <Route
            path="/:languageParam/assessment/sample-questions"
            element={<SampleQuestions />}
          />
          <Route
            path="/:languageParam/assessment/report/:assessmentId/:applicantId"
            element={<DetailedReport />}
          />
        </Route>
        <Route
          path="/:languageParam/company-login"
          element={
            <PublicCompanyRoute>
              <Login />
            </PublicCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/company-signup"
          element={
            <PublicCompanyRoute>
              <SignupScreen />
            </PublicCompanyRoute>
          }
        />
        <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
        <Route
          path="/:languageParam/verify-otp"
          element={
            <PublicCompanyRoute>
              <VerifyOtpScreen />
            </PublicCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/create-password"
          element={
            <PublicCompanyRoute>
              <CreatePasswordScreen />
            </PublicCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/create-profile"
          element={
            <PrivateCompanyRoute>
              <CreateProfileScreen />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/change-password"
          element={
            <PrivateCompanyRoute>
              <ChangePassword />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/forgot-password"
          element={
            <PublicCompanyRoute>
              <ForgotPassword />
            </PublicCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/reset-password"
          element={
            <PublicCompanyRoute>
              <ResetPassword />
            </PublicCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/account/*"
          element={
            <PrivateCompanyRoute>
              <AccountScreen />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/settings"
          element={
            <PrivateCompanyRoute>
              <Settings />
            </PrivateCompanyRoute>
          }
        />

        <Route
          path="/:languageParam/developer-settings/integrations/*"
          element={
            <PrivateCompanyRoute>
              <Integration />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/confirm3dotp"
          element={
            <PrivateCompanyRoute>
              <StripeConfirmOtp />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/jobs"
          element={
            <PrivateCompanyRoute>
              <CvScanning />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/Vendors"
          element={
            <PrivateCompanyRoute>
              <AllVendors />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/documentReview/:partnerCompanyId"
          element={
            <PrivateCompanyRoute>
              <VendorDocuments />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/contracts"
          element={
            <PrivateCompanyRoute>
              <Contracts />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/applications"
          element={
            <PrivateCompanyRoute>
              <Application />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/contractDetails/:contractId"
          element={
            <PrivateCompanyRoute>
              <ContractDetailSidebar />
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/job/:jobId"
          element={
            <PrivateCompanyRoute>
              <div className="jobModuleStyle">
                <SideBarTabContent />
              </div>
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/job/create-job"
          element={
            <PrivateCompanyRoute>
              <div className="jobModuleStyle">
                <JobDescription />
              </div>
            </PrivateCompanyRoute>
          }
        />
        <Route
          path="/:languageParam/job/share-job/:jobId"
          element={
            <PrivateCompanyRoute>
              <JobShare />
            </PrivateCompanyRoute>
          }
        />
        <Route path="/jobs/mailbox" element={<Mailbox />} />
        <Route
          path="/:languageParam/404-not-found-error"
          element={<NotFound />}
        />
        <Route
          path="/:languageParam/unauthorized-error"
          element={<Unauthorized />}
        />
        <Route path="/:languageParam/*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

export default App;
